<template>
  <el-dialog
    v-model="visible"
    title="公式编辑器"
    width="800px"
    :close-on-click-modal="false"
    append-to-body
    destroy-on-close
  >
    <el-tabs
      v-model="activeTab"
      class="formula-tabs"
      @tab-click="handleTabClick"
    >
      <el-tab-pane label="可视化编辑" name="keyboard">
        <div class="mode-container">
          <p class="mode-tip"
            >提示：使用下方虚拟键盘或直接输入，支持 LaTeX 语法自动转换。</p
          >
          <math-field
            ref="mathFieldRef"
            class="math-live-editor"
            :value="currentLatex"
            @input="handleMathLiveInput"
          />
        </div>
      </el-tab-pane>

      <el-tab-pane label="LaTeX 源码" name="source">
        <div class="mode-container">
          <p class="mode-tip"
            >提示：在此处直接输入或粘贴 LaTeX 代码，下方将实时显示预览。</p
          >

          <el-input
            v-model="currentLatex"
            type="textarea"
            :rows="6"
            placeholder="例如: \frac{a}{b} \sqrt{x}"
            class="source-input"
            @input="handleSourceInput"
          />

          <div class="latex-preview-box">
            <div class="preview-label">实时预览：</div>
            <div class="preview-content">
              <math-field
                read-only
                class="math-preview-render"
                :value="currentLatex || '\\text{等待输入...}'"
              />
            </div>
          </div>
        </div>
      </el-tab-pane>
    </el-tabs>

    <template #footer>
      <div class="dialog-footer">
        <div class="footer-left">
          <span v-if="currentLatex"
            >当前代码：<code>{{ currentLatex }}</code></span
          >
        </div>
        <div class="footer-right">
          <el-button @click="visible = false">取消</el-button>
          <el-button
            type="primary"
            :disabled="!currentLatex"
            @click="confirmInsert"
          >
            确认插入
          </el-button>
        </div>
      </div>
    </template>
  </el-dialog>
</template>

<script setup lang="ts">
  import { ref, watch, nextTick } from 'vue';
  import 'mathlive';

  const props = defineProps<{
    modelValue: boolean;
  }>();

  const emit = defineEmits(['update:modelValue', 'insert']);

  const visible = ref(false);
  const activeTab = ref('keyboard');
  const currentLatex = ref('');
  const mathFieldRef = ref();

  watch(
    () => props.modelValue,
    (val) => {
      visible.value = val;
      if (val) {
        currentLatex.value = '';
        activeTab.value = 'keyboard';
        nextTick(() => {
          focusEditor();
        });
      }
    }
  );

  watch(visible, (val) => {
    emit('update:modelValue', val);
  });

  const focusEditor = () => {
    if (activeTab.value === 'keyboard' && mathFieldRef.value) {
      mathFieldRef.value.focus();
    }
  };

  const handleTabClick = () => {
    nextTick(() => {
      focusEditor();
    });
  };

  const handleMathLiveInput = (e: any) => {
    currentLatex.value = e.target.value;
  };

  const handleSourceInput = (val: string) => {
    currentLatex.value = val;
  };

  const confirmInsert = () => {
    if (!currentLatex.value) return;
    emit('insert', currentLatex.value);
    visible.value = false;
  };
</script>

<style scoped>
  .mode-container {
    padding: 10px 0;
  }

  .mode-tip {
    font-size: 13px;
    color: #909399;
    margin-bottom: 12px;
  }

  .source-input {
    margin-bottom: 20px;
  }

  .math-live-editor,
  .math-preview-render {
    width: 100%;
    min-height: 60px;
    display: block;
    outline: none;
  }

  .math-live-editor {
    font-size: 22px;
    padding: 12px;
    border: 1px solid #dcdfe6;
    border-radius: 4px;
    background: #fff;
    box-sizing: border-box;
    --keyboard-zindex: 3000;
  }

  .math-live-editor:focus-within {
    border-color: #409eff;
    box-shadow: 0 0 0 2px rgba(64, 158, 255, 0.2);
  }

  .math-preview-render {
    background: transparent;
    border: none;
    font-size: 20px;
  }

  .latex-preview-box {
    background-color: #f8f9fa;
    border: 1px dashed #dcdfe6;
    border-radius: 4px;
    padding: 15px;
    min-height: 100px;
    display: flex;
    flex-direction: column;
  }

  .preview-label {
    font-size: 12px;
    color: #909399;
    margin-bottom: 10px;
    font-weight: bold;
  }

  .preview-content {
    flex: 1;
    display: flex;
    align-items: center;
    justify-content: center;
  }

  .dialog-footer {
    display: flex;
    justify-content: space-between;
    align-items: center;
  }

  .footer-left {
    font-size: 12px;
    color: #909399;
    max-width: 450px;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
  }

  .footer-left code {
    background: #f4f4f5;
    padding: 2px 4px;
    border-radius: 3px;
    margin-left: 5px;
    color: #409eff;
  }
</style>

<style>
  body {
    --keyboard-zindex: 10000 !important;
  }
  math-field::part(virtual-keyboard) {
    z-index: 10000;
  }
</style>
